import { Add, Delete, Edit } from "@mui/icons-material";
import {
  Box,
  BoxProps,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useRef, useState } from "react";
import { DeletePDFTemplate } from "../../Common/CommonDialogs";
import CreatePDFTemplate from "../../Common/CreatePDFTemplate";
import { PartnerCommsContext } from "../../Context/PartnerCommunicationContext";
import { NdAsyncButton } from "../../MsmePartnerPortal/CommonComponents";

export const DialogSectionTitleBar = ({ title }: { title: string }) => {
  return (
    <Box sx={{ px: 2, py: 1, bgcolor: "#f3f3f3", borderTop: 1, borderColor: "#dedede" }}>
      <Typography variant="caption" color="textSecondary">
        {title}
      </Typography>
    </Box>
  );
};

export const DialogSection = (props: BoxProps) => {
  return (
    <Box sx={{ p: 2 }} {...props}>
      {props.children}
    </Box>
  );
};

export const RenderCommunicationOptions = (props: {
  name: typeof NotConfirmingBCOptions[number]["name"] | typeof LedgerReqOptions[number]["name"];
  description: string;
  value: OptionValues;
  setValue: (value: OptionValues) => void;
  required?: boolean;
  setOpenPDFTemplate: React.Dispatch<React.SetStateAction<boolean>>;
  pdfTemplateId: number | undefined;
  setPdfTemplateId: React.Dispatch<React.SetStateAction<number | undefined>>;
  listUserEmailTemplates: () => void;
}) => {
  const { pdfTemplates } = useContext(PartnerCommsContext);

  const [openPDFTemplate, setOpenPDFTemplate] = useState(false);
  const [OpenDeleteModal, setOpenDeleteModal] = useState(false);
  const currentTemplateName = useRef("");
  const currentTemplateId = useRef<number>();

  return (
    <React.Fragment key={props.name}>
      <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">{props.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            {props.description}
          </Typography>
        </Box>
        <FormControl disabled={props.required}>
          <RadioGroup
            row
            onChange={(v) => {
              props.setValue(v.target.value as OptionValues);
            }}
          >
            <FormControlLabel
              value="yes"
              control={<Radio />}
              label="Yes (Mandatory)"
              checked={props.required || props.value === "yes"}
            />
            <FormControlLabel
              value="optional"
              control={<Radio />}
              label="Yes (Optional)"
              checked={props.value === "optional"}
            />
            <FormControlLabel value="no" control={<Radio />} label="No" checked={props.value === "no"} />
          </RadioGroup>
        </FormControl>
      </Box>
      {props.name === "PDF" && (props.value === "optional" || props.value === "yes") && (
        <Box display="flex" alignItems="center" gap={2} px={2} py={1} bgcolor="#f8f8f8" borderRadius={1}>
          <Typography variant="subtitle2" color="textPrimary">
            Dynamic PDF Template
          </Typography>
          <TextField
            select
            variant="outlined"
            size="small"
            defaultValue=""
            sx={{ minWidth: 200 }}
            value={props.pdfTemplateId || ""}
            SelectProps={{
              renderValue: (_value) =>
                pdfTemplates.find((item) => item.templateId === props.pdfTemplateId)?.templateName || "",
            }}
            onChange={async (e) => {
              const filteredSelectedTemplate = pdfTemplates.filter((item) => {
                return item.templateId === Number(e.target.value);
              });

              const { templateId } = filteredSelectedTemplate[0];

              props.setPdfTemplateId(templateId);
            }}
          >
            {pdfTemplates.length > 0 &&
              pdfTemplates.map((item, itemInd: number) => (
                <MenuItem key={itemInd} value={item.templateId} className="vertical_center_align">
                  {item.templateName}
                  <NdAsyncButton
                    variant="text"
                    size="small"
                    onClick={async (_e) => {
                      _e.stopPropagation();
                      currentTemplateName.current = item.templateName;
                      setOpenPDFTemplate(true);
                    }}
                    buttonComponent={IconButton}
                    sx={{ ml: "auto" }}
                  >
                    <Edit />
                  </NdAsyncButton>
                  <NdAsyncButton
                    variant="text"
                    size="small"
                    onClick={async (_e) => {
                      _e.stopPropagation();
                      currentTemplateId.current = item.templateId;
                      setOpenDeleteModal(true);
                    }}
                    buttonComponent={IconButton}
                    sx={{ ml: 0 }}
                    color="error"
                  >
                    <Delete />
                  </NdAsyncButton>
                </MenuItem>
              ))}
            <MenuItem value="">Select Template</MenuItem>
          </TextField>
          <Button
            variant="text"
            startIcon={<Add />}
            sx={{ textTransform: "none" }}
            onClick={() => {
              // Open CreatePDFTemplate
              props.setOpenPDFTemplate(true);
            }}
          >
            NEW TEMPLATE
          </Button>
          <CreatePDFTemplate
            open={openPDFTemplate}
            setOpen={setOpenPDFTemplate}
            selectedTemplateName={currentTemplateName.current || ""}
            listUserEmailTemplates={props.listUserEmailTemplates}
          />
          <DeletePDFTemplate
            open={OpenDeleteModal}
            setOpen={setOpenDeleteModal}
            templateId={currentTemplateId.current}
            listUserEmailTemplates={props.listUserEmailTemplates}
          />
        </Box>
      )}
    </React.Fragment>
  );
};

export type OptionKeys = typeof BalanceConfirmationOptions[number]["key"];
export type OptionValues = "yes" | "no" | "optional";

export type OptionKeysLR = typeof LedgerReqOptions[number]["key"];

export const BalanceConfirmationOptions = [
  {
    key: "pdf",
    name: "PDF",
    description:
      "A confirmation document that has to be signed and stamped by the partner to validate their closing balance",
  },
  {
    key: "ledger",
    name: "Ledger",
    description: "Full Ledger of the partner till the closing balance date which is needed for reconciliation",
  },
  {
    key: "openItem",
    name: "Open Items",
    description: "List of Invoices that are not paid yet or Payment is not cleared",
  },
] as const;

export const NotConfirmingBCOptions = [
  {
    key: "closingBalance",
    name: "Closing Balance",
    description: "Closing Balance of the partner on the specified date according to their ledger",
    required: true,
  },
  ...BalanceConfirmationOptions,
] as const;

export const LedgerReqOptions = [
  {
    key: "ledger",
    name: "Ledger",
    description: "Full Ledger of the partner till the closing balance date which is needed for reconciliation",
  },
  {
    key: "partnerBalance",
    name: "Ask Partner Balance",
    description: "Closing Balance of the partner on the specified date according to their ledger",
  },
  {
    key: "openItem",
    name: "Open Items",
    description: "List of Invoices that are not paid yet or Payment is not cleared",
  },
] as const;
